import {z} from '@hono/zod-openapi';

import {amount} from './amount';

/**
 * One line of the breakdown: the value without VAT booked by a kind of document in the
 * period and how many documents booked it. `null` when a movement without a value makes the
 * figure unknown, like a month in the chart.
 */
const MovementsBreakdownItemSchema = z.object({
  value: amount.pipe(z.number().nonnegative()).nullable(),
  documentCount: z.coerce.number().int().nonnegative(),
});

export const WarehouseMovementsResponseSchema = z
  .object({
    data: z.array(
      z.object({
        month: z.string().regex(/^\d{4}-\d{2}$/),
        receipts: amount.pipe(z.number().nonnegative()).nullable(),
        issues: amount.pipe(z.number().nonnegative()).nullable(),
        // Signed, unlike receipts and issues: a correction can raise or lower the stock
        // value, and which way it went is the reason the metric exists.
        corrections: amount.nullable(),
        difference: amount.nullable(),
        movementCount: z.coerce.number().int().nonnegative(),
      })
    ),
    // The whole requested period, not a month: the lines of a side add up to that side's
    // total in `data`. `other` holds what no named document explains, such as an opening
    // balance, so the sum still holds.
    breakdown: z.object({
      receipts: z.object({
        suppliers: MovementsBreakdownItemSchema,
        transfers: MovementsBreakdownItemSchema,
        returns: MovementsBreakdownItemSchema,
        other: MovementsBreakdownItemSchema,
      }),
      issues: z.object({
        orders: MovementsBreakdownItemSchema,
        transfers: MovementsBreakdownItemSchema,
        other: MovementsBreakdownItemSchema,
      }),
    }),
  })
  .openapi('WarehouseMovementsResponse');

export type WarehouseMovementsResponse = z.infer<typeof WarehouseMovementsResponseSchema>;
